/**
 * Product Price with Fees Manager
 * Tracks selected custom options with fees and updates the product price display accordingly
 */

class ProductPriceWithFeesManager {
  constructor() {
    this.feeProductPrices = new Map(); // Cache for fee product prices
    this.quantityDebounceTimer = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.attachListeners());
    } else {
      this.attachListeners();
    }

    // Re-attach listeners after section reload
    document.addEventListener('shopify:section:load', () => {
      this.attachListeners();
    });

    document.addEventListener('hatlabDesignSelected', () => this.updateAllForms());
    document.addEventListener('hatlabDesignCleared', () => this.updateAllForms());
    document.addEventListener('productPropertyValueChange', () => this.updateAllForms());
    document.addEventListener('tier-pricing:update', (event) => {
      const section = event.target?.closest?.('.shopify-section');
      section?.querySelectorAll('product-form-component form').forEach((form) => {
        if (form instanceof HTMLFormElement) this.handleFeeChange(form);
      });
    });
  }

  updateAllForms() {
    document.querySelectorAll('product-form-component form').forEach((form) => {
      if (form instanceof HTMLFormElement) {
        this.handleFeeChange(form);
      }
    });
  }

  attachListeners() {
    // Find all product forms
    const productForms = document.querySelectorAll('product-form-component');
    
    productForms.forEach(form => {
      const formElement = form.querySelector('form');
      if (!formElement) return;

      // Listen for changes on all inputs within fee-enabled blocks
      const feeBlocks = this.getFeeBlocks(formElement);
      if (!feeBlocks.length) return;

      // Skip if already initialized
      if (form.dataset.feePriceInitialized) return;
      form.dataset.feePriceInitialized = 'true';
      
      feeBlocks.forEach(block => {
        // Add listeners to all inputs within this block
        const inputs = block.querySelectorAll('input, select, textarea');
        inputs.forEach(input => {
          if (!input.dataset.feePriceListenerAttached) {
            input.addEventListener('change', () => this.handleFeeChange(formElement));
            input.addEventListener('input', () => this.handleFeeChange(formElement));
            input.dataset.feePriceListenerAttached = 'true';
          }
        });
      });

      // Listen for quantity changes at the form level for better reliability
      formElement.addEventListener('quantity-selector:update', () => {
        // Clear any pending debounce
        if (this.quantityDebounceTimer) {
          clearTimeout(this.quantityDebounceTimer);
        }
        // Immediate update on quantity selector component events
        this.handleFeeChange(formElement);
      });

      // Also listen on the input directly as a fallback
      const quantityInput = formElement.querySelector('input[name="quantity"]');
      if (quantityInput && !quantityInput.dataset.feePriceQuantityListenerAttached) {
        quantityInput.addEventListener('change', () => {
          // Clear any pending debounce
          if (this.quantityDebounceTimer) {
            clearTimeout(this.quantityDebounceTimer);
          }
          // Immediate update on change event
          this.handleFeeChange(formElement);
        });
        quantityInput.addEventListener('input', () => {
          // Debounce input events to avoid excessive recalculation
          if (this.quantityDebounceTimer) {
            clearTimeout(this.quantityDebounceTimer);
          }
          this.quantityDebounceTimer = setTimeout(() => {
            this.handleFeeChange(formElement);
          }, 300);
        });
        
        // Use MutationObserver to catch value changes that don't trigger events
        // (e.g., when stepUp/stepDown are called programmatically)
        const observer = new MutationObserver(() => {
          if (this.quantityDebounceTimer) {
            clearTimeout(this.quantityDebounceTimer);
          }
          this.quantityDebounceTimer = setTimeout(() => {
            this.handleFeeChange(formElement);
          }, 100);
        });
        
        observer.observe(quantityInput, {
          attributes: true,
          attributeFilter: ['value']
        });
        
        // Also observe the value property changes
        let lastValue = quantityInput.value;
        const checkValue = () => {
          if (quantityInput.value !== lastValue) {
            lastValue = quantityInput.value;
            if (this.quantityDebounceTimer) {
              clearTimeout(this.quantityDebounceTimer);
            }
            this.quantityDebounceTimer = setTimeout(() => {
              this.handleFeeChange(formElement);
            }, 100);
          }
        };
        
        // Poll for value changes as a last resort fallback
        setInterval(checkValue, 500);
        
        quantityInput.dataset.feePriceQuantityListenerAttached = 'true';
      }

      // Initial calculation - use setTimeout to ensure DOM is fully ready
      setTimeout(() => {
        this.handleFeeChange(formElement);
      }, 100);
    });
  }

  /**
   * Handle fee selection change
   * @param {HTMLFormElement} form 
   */
  async handleFeeChange(form) {
    const productId = form.closest('product-form-component')?.dataset.productId;
    if (!productId) return;

    const grid = form.querySelector('variant-quantity-grid');
    const generation = grid?.active ? (grid.feeEstimateGeneration = (grid.feeEstimateGeneration || 0) + 1) : 0;
    // Collect all selected fees
    const fees = await this.collectSelectedFees(form);
    
    // Calculate total fee amount
    let totalFeeAmount = 0;
    for (const fee of fees) {
      totalFeeAmount += fee.price;
    }

    // Update the price display
    if (grid?.active) {
      if (generation !== grid.feeEstimateGeneration) return;
      grid.setFeeEstimate(totalFeeAmount, fees);
      return;
    }
    this.updatePriceDisplay(productId, totalFeeAmount);
  }

  /**
   * Collect all currently selected fees from the form
   * @param {HTMLFormElement} form 
   * @returns {Promise<Array<{handle: string, price: number}>>}
   */
  async collectSelectedFees(form) {
    const fees = [];
    const formData = new FormData(form);
    const blocks = this.getFeeBlocks(form);
    
    // Get product quantity from the form - try multiple methods
    let productQuantity = parseInt(formData.get('quantity'), 10);
    
    // Fallback: check for quantity input directly
    if (!productQuantity || isNaN(productQuantity)) {
      const quantityInput = form.querySelector('input[name="quantity"]');
      productQuantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
    }
    
    // Final fallback
    if (!productQuantity || isNaN(productQuantity)) {
      productQuantity = 1;
    }

    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i];
      const feeConfig = this.parseFeeConfig(block);
      const blockType = block.dataset.feeType;
      const propertyKey = block.dataset.key || block.dataset.feePropertyKey;

      if (!feeConfig || !feeConfig.enabled) {
        continue;
      }

      switch (blockType) {
        case 'checkbox': {
          const checkbox = block.querySelector('input[type="checkbox"]');
          if (checkbox && checkbox.checked && feeConfig.productHandle) {
            const feeData = this.parseFeeData(feeConfig, productQuantity);
            if (feeData) {
              const price = feeData.price ?? await this.getFeeProductPrice(feeData.handle);
              if (price !== null) {
                fees.push({ handle: feeData.handle, price: price * feeData.quantity, unitPrice: price, quantity: feeData.quantity });
              }
            }
          }
          break;
        }

        case 'text': {
          const value = this.getPropertyValue(form, propertyKey, block);
          if (value && value.toString().trim() !== '' && feeConfig.productHandle) {
            const feeData = this.parseFeeData(feeConfig, productQuantity);
            if (feeData) {
              const price = feeData.price ?? await this.getFeeProductPrice(feeData.handle);
              if (price !== null) {
                fees.push({ handle: feeData.handle, price: price * feeData.quantity, unitPrice: price, quantity: feeData.quantity });
              }
            }
          }
          break;
        }

        case 'select':
        case 'radio': {
          const value = this.getPropertyValue(form, propertyKey, block);
          if (value && value.toString().trim() !== '' && feeConfig.optionFees) {
            const optionFeeConfig = this.getSelectedInputFeeConfig(block) || this.lookupOptionFee(feeConfig.optionFees, value);
            if (optionFeeConfig) {
              const feeData = this.parseFeeData(optionFeeConfig, productQuantity);
              if (feeData) {
                const price = feeData.price ?? await this.getFeeProductPrice(feeData.handle);
                if (price !== null) {
                  fees.push({ handle: feeData.handle, price: price * feeData.quantity, unitPrice: price, quantity: feeData.quantity });
                }
              }
            }
          }
          break;
        }

        case 'multiselect':
        case 'swatch-checkbox': {
          const checkedFeeInputs = block.querySelectorAll('input[data-fee-handle]:checked');
          if (checkedFeeInputs.length > 0) {
            for (const input of checkedFeeInputs) {
              const optionFeeConfig = this.getFeeConfigFromInput(input);
              if (optionFeeConfig) {
                const feeData = this.parseFeeData(optionFeeConfig, productQuantity);
                if (feeData) {
                  const price = feeData.price ?? await this.getFeeProductPrice(feeData.handle);
                  if (price !== null) {
                    fees.push({ handle: feeData.handle, price: price * feeData.quantity, unitPrice: price, quantity: feeData.quantity });
                  }
                }
              }
            }
            break;
          }

          const value = this.getPropertyValue(form, propertyKey, block);
          if (value && feeConfig.optionFees) {
            const selectedOptions = value.toString().split(',').map((entry) => this.normalizeOptionKey(entry)).filter(Boolean);
            for (const option of selectedOptions) {
              const optionFeeConfig = this.lookupOptionFee(feeConfig.optionFees, option);
              if (optionFeeConfig) {
                const feeData = this.parseFeeData(optionFeeConfig, productQuantity);
                if (feeData) {
                  const price = feeData.price ?? await this.getFeeProductPrice(feeData.handle);
                  if (price !== null) {
                    fees.push({ handle: feeData.handle, price: price * feeData.quantity, unitPrice: price, quantity: feeData.quantity });
                  }
                }
              }
            }
          }
          break;
        }

        case 'file': {
          const fileInput = block.querySelector('input[type="file"]');
          if (fileInput?.files?.length > 0 && feeConfig.productHandle) {
            const feeData = this.parseFeeData(feeConfig, productQuantity);
            if (feeData) {
              const price = feeData.price ?? await this.getFeeProductPrice(feeData.handle);
              if (price !== null) {
                fees.push({ handle: feeData.handle, price: price * feeData.quantity, unitPrice: price, quantity: feeData.quantity });
              }
            }
          }
          break;
        }

        case 'personalization': {
          const textKey = block.dataset.personalizationTextKey;
          const textValue = textKey ? this.getPropertyValue(form, textKey, block) : null;
          const fileInput = block.querySelector('input[type="file"]');
          const hasText = textValue && textValue.toString().trim() !== '';
          const hasFile = fileInput?.files?.length > 0;

          if ((hasText || hasFile) && feeConfig.productHandle) {
            const feeData = this.parseFeeData(feeConfig, productQuantity);
            if (feeData) {
              const price = feeData.price ?? await this.getFeeProductPrice(feeData.handle);
              if (price !== null) {
                fees.push({ handle: feeData.handle, price: price * feeData.quantity, unitPrice: price, quantity: feeData.quantity });
              }
            }
          }
          break;
        }
      }
    }

    return fees;
  }

  /**
   * Find fee-enabled blocks that are either inside the form or associated to it
   * with a form id.
   * @param {HTMLFormElement} form
   * @returns {HTMLElement[]}
   */
  getFeeBlocks(form) {
    const blocks = new Set(form.querySelectorAll('[data-fee-config]'));

    const formId = form.getAttribute('id');
    if (formId) {
      const escapedFormId = window.CSS && CSS.escape ? CSS.escape(formId) : formId;
      document.querySelectorAll(`[data-fee-config][data-form="${escapedFormId}"]`).forEach(block => {
        blocks.add(block);
      });
    }

    return Array.from(blocks);
  }

  /**
   * Normalize option labels for fee lookup (handles NBSP and odd spacing).
   * @param {string} value
   * @returns {string}
   */
  normalizeOptionKey(value) {
    if (value == null || value === '') return '';

    return value
      .toString()
      .replace(/\u00a0/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  normalizeOptionSlug(value) {
    return this.normalizeOptionKey(value).replace(/\s+/g, '_');
  }

  /**
   * @param {Object} optionFees
   * @param {string} value
   * @returns {Object|null}
   */
  lookupOptionFee(optionFees, value) {
    if (!optionFees || !value) return null;

    const key = this.normalizeOptionKey(value);
    if (key && optionFees[key]) {
      return optionFees[key];
    }

    const slug = this.normalizeOptionSlug(value);
    if (slug && optionFees[slug]) {
      return optionFees[slug];
    }

    for (const [optionKey, optionFee] of Object.entries(optionFees)) {
      if (this.normalizeOptionKey(optionKey) === key || this.normalizeOptionSlug(optionKey) === slug) {
        return optionFee;
      }
    }

    return null;
  }

  /**
   * @param {string} left
   * @param {string} right
   * @returns {boolean}
   */
  optionValuesMatch(left, right) {
    return this.normalizeOptionKey(left) === this.normalizeOptionKey(right);
  }

  /**
   * Read a line item property value from the form, including controls that are
   * rendered outside the form but linked with form="...".
   * @param {HTMLFormElement} form
   * @param {string} propertyKey
   * @param {HTMLElement} block
   * @returns {string}
   */
  getPropertyValue(form, propertyKey, block) {
    if (!propertyKey) return '';

    const fieldName = `properties[${propertyKey}]`;

    if (block.classList?.contains('design-selector')) {
      const selectorValue = this.getDesignSelectorControlValue(block, fieldName);
      if (selectorValue) {
        return selectorValue;
      }
    }

    const formData = new FormData(form);
    const formValues = formData.getAll(fieldName).filter((value) => value?.toString().trim() !== '');

    if (formValues.length > 0) {
      return formValues.join(', ');
    }

    const controls = this.getPropertyControls(form, fieldName, block);
    const values = [];

    controls.forEach((control) => {
      if (control.disabled) return;

      if ((control.type === 'checkbox' || control.type === 'radio') && !control.checked) {
        return;
      }

      if (control.value?.toString().trim() !== '') {
        values.push(control.value);
      }
    });

    if (values.length > 0) {
      return values.join(', ');
    }

    const designValue = this.getDesignSelectorValue(block);
    if (designValue) {
      return designValue;
    }

    return '';
  }

  /**
   * Read the current Product Design Selector value from its own controls first.
   * @param {HTMLElement} block
   * @param {string} fieldName
   * @returns {string}
   */
  getDesignSelectorControlValue(block, fieldName) {
    const hiddenInput = Array.from(block.querySelectorAll('input[type="hidden"]')).find(
      (input) => input.name === fieldName
    );
    if (hiddenInput && !hiddenInput.disabled && hiddenInput.value?.toString().trim() !== '') {
      return hiddenInput.value.toString().trim();
    }

    const checkedValues = Array.from(block.querySelectorAll('.design-selector__input:checked'))
      .filter((input) => !input.disabled)
      .map((input) => input.value?.toString().trim())
      .filter(Boolean);

    return checkedValues.join(', ');
  }

  /**
   * Product Design Selector can be driven by Hat Lab state before its radio
   * value is synced. Use the same selected design source as the display block.
   * @param {HTMLElement} block
   * @returns {string}
   */
  getDesignSelectorValue(block) {
    if (!block.classList?.contains('design-selector')) return '';

    try {
      const selectedDesign = window.HATLAB_DESIGN?.getSelectedDesign?.(block);
      return selectedDesign?.title?.toString().trim() || '';
    } catch (error) {
      return '';
    }
  }

  /**
   * Read fee metadata directly from the selected option input when present.
   * @param {HTMLElement} block
   * @returns {Object|null}
   */
  getSelectedInputFeeConfig(block) {
    let selectedInput = block.querySelector('input[data-fee-handle]:checked');
    if (!selectedInput && block.classList?.contains('design-selector')) {
      const fieldName = `properties[${block.dataset.key || ''}]`;
      const selectorValue = this.getDesignSelectorControlValue(block, fieldName);
      if (selectorValue) {
        const selectedValues = selectorValue.split(',').map((value) => value.trim());
        selectedInput = Array.from(block.querySelectorAll('input[data-fee-handle]')).find(
          (input) => selectedValues.some((value) => this.optionValuesMatch(input.value, value))
        );
      }

      if (!selectedInput) {
        const designValue = this.getDesignSelectorValue(block);
        if (designValue) {
          selectedInput = Array.from(block.querySelectorAll('input[data-fee-handle]')).find(
            (input) => this.optionValuesMatch(input.value, designValue)
          );
        }
      }
    }
    if (!selectedInput) return null;

    return this.getFeeConfigFromInput(selectedInput);
  }

  /**
   * Read fee metadata directly from an option input when present.
   * @param {HTMLInputElement} input
   * @returns {Object|null}
   */
  getFeeConfigFromInput(input) {
    if (!input) return null;

    const handle = input.dataset.feeHandle?.trim();
    if (!handle) return null;

    const config = {
      handle,
      type: input.dataset.feeType || 'one-time'
    };

    if (input.dataset.feeMinQtyWaive) {
      config.minQtyWaive = input.dataset.feeMinQtyWaive;
    }

    if (input.dataset.feeVariantId) {
      config.variantId = input.dataset.feeVariantId;
    }

    if (input.dataset.feePrice) {
      const price = Number(input.dataset.feePrice);
      if (!Number.isNaN(price)) {
        config.price = price;
      }
    }

    return config;
  }

  /**
   * Find controls for a property inside the block, the form, or associated by
   * matching form id.
   * @param {HTMLFormElement} form
   * @param {string} fieldName
   * @param {HTMLElement} block
   * @returns {HTMLElement[]}
   */
  getPropertyControls(form, fieldName, block) {
    const controls = new Set();
    const candidates = [
      ...Array.from(form.querySelectorAll('input, select, textarea')),
      ...Array.from(block.querySelectorAll('input, select, textarea'))
    ];

    if (form.id) {
      document.querySelectorAll('input, select, textarea').forEach((control) => {
        if (control.getAttribute('form') === form.id) {
          candidates.push(control);
        }
      });
    }

    candidates.forEach((control) => {
      if (control.name === fieldName) {
        controls.add(control);
      }
    });

    return Array.from(controls);
  }

  /**
   * Parse fee data and determine quantity
   * @param {Object|string} feeConfig - Fee configuration (can be string or object)
   * @param {number} productQuantity - Product quantity
   * @returns {Object|null} - {handle: string, price: number|null, quantity: number} or null if waived
   */
  parseFeeData(feeConfig, productQuantity) {
    // Handle both old format (string) and new format (object)
    let handle, type, minQtyWaive, price;
    
    if (typeof feeConfig === 'string') {
      // Old format: just the product handle
      handle = feeConfig;
      type = 'one-time';
      minQtyWaive = null;
      price = null;
    } else {
      // New format: object with handle, type, and optional minQtyWaive
      handle = feeConfig.handle || feeConfig.productHandle;
      type = (feeConfig.type || 'one-time').toLowerCase().trim();
      minQtyWaive = feeConfig.minQtyWaive || null;
      price = typeof feeConfig.price === 'number' ? feeConfig.price : null;
    }
    
    if (!handle) return null;

    // Check if fee should be waived based on quantity threshold
    if (minQtyWaive) {
      const threshold = parseInt(minQtyWaive, 10);
      if (!isNaN(threshold) && productQuantity >= threshold) {
        return null; // Skip this fee
      }
    }

    // Calculate fee quantity based on type (case-insensitive)
    let feeQuantity = 1;
    if (type === 'per-item') {
      feeQuantity = productQuantity;
    }

    return { handle, price, quantity: feeQuantity };
  }

  /**
   * Parse fee configuration from block element
   * @param {HTMLElement} block 
   * @returns {Object|null}
   */
  parseFeeConfig(block) {
    try {
      const jsonScript = block.querySelector('script[data-fee-config-json]');
      if (jsonScript?.textContent?.trim()) {
        return JSON.parse(jsonScript.textContent);
      }

      return JSON.parse(block.dataset.feeConfig || '{}');
    } catch (e) {
      return null;
    }
  }

  /**
   * Get fee product price (with caching)
   * @param {string} handle 
   * @returns {Promise<number|null>}
   */
  async getFeeProductPrice(handle) {
    // Check cache first
    if (this.feeProductPrices.has(handle)) {
      return this.feeProductPrices.get(handle);
    }

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        return null;
      }

      const product = await response.json();
      if (!product.variants || product.variants.length === 0) {
        return null;
      }

      // Get price in cents
      const price = product.variants[0].price;
      
      // Cache it
      this.feeProductPrices.set(handle, price);
      
      return price;
    } catch (error) {
      return null;
    }
  }

  /**
   * Update the price display with fees
   * @param {string} productId 
   * @param {number} totalFeeAmount - Total fee amount in cents
   */
  updatePriceDisplay(productId, totalFeeAmount) {
    // Find the product-price element for this product
    const priceElement = document.querySelector(`product-price[data-product-id="${productId}"]`);
    if (!priceElement) return;

    const priceContainer = priceElement.querySelector('[ref="priceContainer"]');
    if (!priceContainer) return;

    // Find the main price element
    const priceSpan = priceContainer.querySelector('.price');
    if (!priceSpan) return;

    const ymqPriceHook = priceSpan.querySelector('[ymq-b2b-variant-price]');
    if (ymqPriceHook) {
      priceContainer.querySelector('.price-fee-indicator')?.remove();
      return;
    }

    const tierPrice = priceSpan.dataset.tptUnitPrice;
    const tierPriceCents = tierPrice !== undefined && tierPrice !== '' ? Number(tierPrice) : NaN;
    const hasTierPrice = Number.isFinite(tierPriceCents) && tierPriceCents >= 0;

    // Cache only when applying fees; an unchanged price may be owned by an app.
    if (totalFeeAmount !== 0 && !hasTierPrice && !priceSpan.dataset.originalPrice) {
      priceSpan.dataset.originalPrice = priceSpan.textContent.trim();
    }

    // If no fees, restore original price
    if (totalFeeAmount === 0) {
      if (hasTierPrice) {
        priceSpan.textContent = priceSpan.dataset.tptUnitPriceText ?? this.formatPrice(tierPriceCents);
      } else if (priceSpan.dataset.originalPrice !== undefined) {
        priceSpan.textContent = priceSpan.dataset.originalPrice;
      }
      delete priceSpan.dataset.originalPrice;
      // Remove any fee indicator
      const feeIndicator = priceContainer.querySelector('.price-fee-indicator');
      if (feeIndicator) {
        feeIndicator.remove();
      }
      return;
    }

    // Get the original price value in cents (we need to parse it)
    const originalText = priceSpan.dataset.originalPrice;
    const originalPriceCents = hasTierPrice ? tierPriceCents : this.parsePriceToCents(originalText);
    
    if (originalPriceCents === null) return;

    // Calculate new total
    const newTotalCents = originalPriceCents + totalFeeAmount;
    
    // Format the new price
    const newPriceFormatted = this.formatPrice(newTotalCents);
    
    // Update the price
    priceSpan.textContent = newPriceFormatted;

    // Add a fee indicator if it doesn't exist
    let feeIndicator = priceContainer.querySelector('.price-fee-indicator');
    if (!feeIndicator) {
      feeIndicator = document.createElement('span');
      feeIndicator.className = 'price-fee-indicator';
      feeIndicator.style.cssText = 'display: block; font-size: 0.85em; color: rgb(var(--color-foreground-rgb) / 0.7); margin-top: 0.25rem;';
      priceContainer.appendChild(feeIndicator);
    }
    
    const feeFormatted = this.formatPrice(totalFeeAmount);
    feeIndicator.textContent = `(Includes ${feeFormatted} in fees)`;
  }

  /**
   * Parse a price string to cents
   * @param {string} priceText 
   * @returns {number|null}
   */
  parsePriceToCents(priceText) {
    // Remove currency symbols and whitespace
    const cleaned = priceText.replace(/[^0-9.,]/g, '');
    
    // Handle different decimal separators
    // Check if comma is used as decimal separator (European format)
    if (cleaned.includes(',') && !cleaned.includes('.')) {
      // Format: 1.234,56 or 1234,56
      const withoutThousandsSep = cleaned.replace(/\./g, '');
      const normalized = withoutThousandsSep.replace(',', '.');
      return Math.round(parseFloat(normalized) * 100);
    } else {
      // Format: 1,234.56 or 1234.56 (US format)
      const normalized = cleaned.replace(/,/g, '');
      return Math.round(parseFloat(normalized) * 100);
    }
  }

  /**
   * Format cents to money string
   * @param {number} cents 
   * @returns {string}
   */
  formatPrice(cents) {
    // Use Shopify's money format if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }
    
    // Fallback formatting
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }
}

// Initialize the manager
if (!window.productPriceWithFeesManager) {
  window.productPriceWithFeesManager = new ProductPriceWithFeesManager();
}
